import { I18nService } from './../../../../@core/utils/i18n.service';
import { DatePipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { NbDialogService, NbCardModule, NbAlertModule, NbButtonModule } from '@nebular/theme';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import * as Highcharts from 'highcharts';
import { combineLatest, Subscription } from 'rxjs';
import { KPI } from '../../../../@core/api';
import { LayoutService, ProjectsService, YearsService } from '../../../../@core/utils';
import { BaseChartComponent } from '../base-chart/base-chart.component';
import { HighchartsChartModule } from 'highcharts-angular';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

@Component({
    selector: 'app-asset-projects-timeline',
    styleUrls: ['./asset-projects-timeline.component.scss'],
    templateUrl: '../base-chart/base-chart.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        NbCardModule,
        NgClass,
        
        FontAwesomeModule,
        HighchartsChartModule,
        NbAlertModule,
        NbButtonModule,
        TranslateModule,
    ],
})
export class AssetProjectsTimelineComponent
  extends BaseChartComponent
  implements OnInit, OnChanges, OnDestroy {
  @Input() public asset: KPI.MappedAsset;

  private subscriptions: Subscription[] = [];
  public startYear: number;

  constructor(
    protected dialogService: NbDialogService,
    protected layoutService: LayoutService,
    protected router: Router,
    private projectsService: ProjectsService,
    private translate: TranslateService,
    private i18n: I18nService,
    private cdRef: ChangeDetectorRef,
    private yearsService: YearsService,
  ) {
    super(dialogService, layoutService, router);
  }

  public ngOnInit(): void {
    super.ngOnInit();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.asset) {
      if (this.asset) {
        this.update();
      }
    }
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  protected update(): void {
    if (this.asset) {
      this.subscriptions.push(
        combineLatest([
          this.projectsService.getAssetProjects(this.asset),
          this.yearsService.startYear$,
        ]).subscribe(([projects, start_year]) => {

          this.startYear = start_year;
          if (!projects || !projects.length) {
            this.visible = false;
            this.loading = false;
            this.cdRef.detectChanges();
          } else {
            const pipe = new DatePipe(this.i18n.getCulture());
            // Initialize series for each statuses
            const datas = projects.sort((a, b) =>
              new Date(a.start_date).getTime() - new Date(b.start_date).getTime()
            ).map((project) => {
              const startDate = pipe.transform(new Date(project.start_date), 'MMM y');
              const endDate = pipe.transform(new Date(project.end_date), 'MMM y');
              let label = `<span style="color:gray;">(${project.label})</span><br>${startDate} - ${endDate}`;
              const assetIsInProject: boolean = !!project.assets.find((a) => a.id === this.asset.id);
              const nextAssetInProjects: boolean = !!project.next_assets.find((a) => a.id === this.asset.id);

              if (assetIsInProject || nextAssetInProjects) {
                const includesAsset = this.translate.instant('Includes the asset');
                label += `<br><span style="color:#c00000;font-weight:bold;">${includesAsset}</span>`;
              } else {
                const doesNotIncludeAsset = this.translate.instant('Does not apply to equipment');
                label += `<br><span style="color:#7f7f7f;font-weight:bold;">${doesNotIncludeAsset}</span>`;
              }
              return {
                project_id: project.id,
                name: project.start_date,
                label,
                project_type: project.project_type?.name
              }
            }).filter((elm) => new Date(elm.name).getFullYear() >= this.startYear);

            this.visible = datas.length ? true : false;

            const series = [{
              type: 'timeline',
              data: datas
            }];

            // Update chart options
            this.options = {
              ...this.getOptions(),
              // @ts-ignore
              xAxis: {
                ...this.getOptions().xAxis,
                tickWidth: 0,
              },
              series: <Highcharts.SeriesTimelineOptions[]>series,
            };

            this.loading = false;
            this.reflow();
            this.cdRef.detectChanges();
          }
        })
      )
    }

    this.cdRef.detectChanges();
  }

  protected getOptions(): Highcharts.Options {

    const commonOptions = { colors: [] };

    return {
      ...super.getOptions(),
      ...commonOptions,
      colors: ['#9CAFC4', '#849CB6', '#728EAC', '#5B799A'],
      tooltip: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      plotOptions: {
        timeline: {
          cursor: 'pointer',
          dataLabels: {
            distance: '65',
            formatter: function () {
              return `<b>${this.point.options['project_type']}</b><br>${this.point['label']}`
            },
          }
        },
        series: {
          point: {
            events: {
              click: this.getColumnClickHandler(),
            },
          },
        },
      },
      yAxis: {
        visible: false
      },
      xAxis: {
        visible: false
      },
      series: [],
    };
  }

  protected getChartOptions(): Highcharts.ChartOptions {
    return {
      ...super.getChartOptions(),
      type: 'timeline',
    };
  }

  private getColumnClickHandler(): (
    event: Highcharts.SeriesClickEventObject,
  ) => void {
    const router = this.router;

    return function (event: Highcharts.SeriesClickEventObject) {
      // Add some timeout for scenarii comparison. In this case navigate is to early and we miss some data without it
      setTimeout(() => {
        router.navigate(['single-pages', 'project', event.point.options['project_id']]);
      }, 300);
    };
  }
}
